import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Mail, Phone, Shield, Save, Users as UsersIcon, CreditCard, Calendar, AlertCircle, CheckCircle, Clock, ArrowLeft, RefreshCw, Lock, KeyRound, Smartphone, ChevronRight, Bell, BellOff, Database } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import StaffManagement from './StaffManagement';
import ActivityLog from './ActivityLog';
import PrivacyPolicy from './PrivacyPolicy';
import { getSubscriptionPlans, getMockPayments } from '../mock';
import { formatPhoneNumber } from '../utils/validation';
import { clearAppCache, forceReload, getAppVersion, checkForUpdates, applyUpdate } from '../utils/versionCheck';
import { subscriptionAPI, otpAPI, emailChangeAPI, notificationsAPI } from '../services/api';
import useFinancialVisibility, { useFinancialVisibilityExtended } from '../hooks/useFinancialVisibility';
import { 
  isPushNotificationSupported, 
  getNotificationPermission, 
  subscribeToPushNotifications, 
  unsubscribeFromPushNotifications,
  isSubscribedToPush 
} from '../services/pushNotifications';
import BackupRestore from './BackupRestore';

// Subscription Badge Component
const SubscriptionBadge = ({ subscription }) => {
  const plans = getSubscriptionPlans();
  const planId = subscription?.plan;
  const plan = plans.find(p => p.id === planId);
  
  // Map plan IDs to badge styles
  const badgeStyles = {
    'PLAN_TRIAL': { name: 'Trial', icon: '🎁', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800', border: 'border-green-300' },
    'PLAN_FREE_TRIAL': { name: 'Trial', icon: '🎁', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800', border: 'border-green-300' },
    'PLAN_1_MONTH': { name: '1 Month', icon: '⭐', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800', border: 'border-blue-300' },
    'PLAN_3_MONTHS': { name: '3 Months', icon: '💎', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800', border: 'border-orange-300' },
    'PLAN_6_MONTHS': { name: '6 Months', icon: '🏅', bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800', border: 'border-pink-300' },
    'PLAN_12_MONTHS': { name: '12 Months', icon: '🏆', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800', border: 'border-amber-300' },
    'PLAN_LIFETIME': { name: 'Lifetime', icon: '👑', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800', border: 'border-purple-300' },
    // Old plan mappings
    'PLAN_FREE': { name: 'Trial', icon: '🎁', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800', border: 'border-green-300' },
    'PLAN_BASIC': { name: '1 Month', icon: '⭐', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800', border: 'border-blue-300' },
    'PLAN_PRO': { name: '3 Months', icon: '💎', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800', border: 'border-orange-300' }
  };

  if (!subscription) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-secondary text-gray-600 dark:text-muted-foreground border border-gray-300 dark:border-border">
        <span>⚪</span>
        <span>No Plan</span>
      </span>
    );
  }

  const style = badgeStyles[planId] || { name: plan?.name || 'Basic', icon: '⭐', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800', border: 'border-blue-300' };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text} border ${style.border}`}>
      <span>{style.icon}</span>
      <span>{style.name}</span>
    </span>
  );
};

const Profile = () => {
  const { currentUser, updateProfile, impersonating, returnToMaster } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || ''
  });
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showFinancialToggle, setShowFinancialToggle] = useState(false);
  const [financialPassword, setFinancialPassword] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const hideFinancials = useFinancialVisibility();
  const { adminHidesFromStaff, isStaffUser } = useFinancialVisibilityExtended();
  
  // Email change with OTP state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  
  // Update-related state
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [applyingUpdate, setApplyingUpdate] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');

  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const supported = isPushNotificationSupported();
      setPushSupported(supported);
      
      if (supported) {
        setPushPermission(getNotificationPermission());
        const subscribed = await isSubscribedToPush();
        setPushEnabled(subscribed);
      }
    };
    checkPushStatus();
  }, []);

  const handleTogglePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        // Unsubscribe
        const result = await unsubscribeFromPushNotifications();
        if (result.success) {
          setPushEnabled(false);
          toast({
            title: 'Push notifications disabled',
            description: 'You will no longer receive push notifications.',
          });
        } else {
          throw new Error(result.error);
        }
      } else {
        // Subscribe
        const result = await subscribeToPushNotifications();
        if (result.success) {
          setPushEnabled(true);
          setPushPermission('granted');
          toast({
            title: 'Push notifications enabled',
            description: 'You will now receive push notifications.',
          });
        } else {
          if (result.error === 'Permission denied') {
            setPushPermission('denied');
            toast({
              title: 'Permission denied',
              description: 'Please enable notifications in your browser settings.',
              variant: 'destructive'
            });
          } else {
            throw new Error(result.error);
          }
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update push notification settings',
        variant: 'destructive'
      });
    } finally {
      setPushLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      
      // First update profile info
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update profile');
      }
      
      // Update local storage with new user data
      const updatedUser = data.user;
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      updateProfile(updatedUser);
      
      // Handle password change if user wants to change it
      if (showPasswordChange && passwordFormData.currentPassword && passwordFormData.newPassword) {
        // Validate passwords
        if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
          toast({ 
            title: 'Error', 
            description: 'New passwords do not match',
            variant: 'destructive'
          });
          return;
        }
        
        if (passwordFormData.newPassword.length < 6) {
          toast({ 
            title: 'Error', 
            description: 'Password must be at least 6 characters',
            variant: 'destructive'
          });
          return;
        }
        
        // Call password change API
        const passwordResponse = await fetch(`${API_URL}/api/auth/change-password`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            currentPassword: passwordFormData.currentPassword,
            newPassword: passwordFormData.newPassword
          })
        });
        
        const passwordData = await passwordResponse.json();
        
        if (!passwordResponse.ok) {
          throw new Error(passwordData.detail || 'Failed to change password');
        }
        
        // Reset password form
        setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordChange(false);
        
        toast({ 
          title: 'Success', 
          description: 'Profile and password updated successfully' 
        });
      } else {
        toast({ 
          title: 'Success', 
          description: 'Profile updated successfully' 
        });
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update profile',
        variant: 'destructive'
      });
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    if (!currentUser?.isMaster) {
      toast({ 
        title: 'Error', 
        description: 'Only Master account can change password',
        variant: 'destructive'
      });
      return;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      toast({ 
        title: 'Error', 
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwordFormData.newPassword.length < 8) {
      toast({ 
        title: 'Error', 
        description: 'Password must be at least 8 characters long',
        variant: 'destructive'
      });
      return;
    }

    // Update password in users mock data
    const users = JSON.parse(localStorage.getItem('mockUsers') || '[]');
    const updatedUsers = users.map(u => 
      u.id === currentUser.id ? { ...u, password: passwordFormData.newPassword } : u
    );
    localStorage.setItem('mockUsers', JSON.stringify(updatedUsers));

    setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordChange(false);
    toast({ 
      title: 'Success', 
      description: 'Password changed successfully' 
    });
  };

  const handleCancel = () => {
    setFormData({
      name: currentUser?.name || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || ''
    });
    setIsEditing(false);
  };

  // Email change with OTP functions
  const handleSendEmailOtp = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    if (newEmail.toLowerCase() === currentUser?.email?.toLowerCase()) {
      toast({
        title: 'Error',
        description: 'New email must be different from current email',
        variant: 'destructive'
      });
      return;
    }

    setEmailOtpLoading(true);
    try {
      await otpAPI.send(newEmail, 'email_change', currentUser?.name || 'User');
      setEmailOtpSent(true);
      toast({
        title: 'OTP Sent',
        description: `Verification code sent to ${newEmail}. Valid for 30 minutes.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send OTP',
        variant: 'destructive'
      });
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtp || emailOtp.length !== 4) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 4-digit OTP',
        variant: 'destructive'
      });
      return;
    }

    setEmailOtpLoading(true);
    try {
      await otpAPI.verify(newEmail, emailOtp, 'email_change');
      setEmailOtpVerified(true);
      toast({
        title: 'OTP Verified',
        description: 'Email verified! Click "Change Email" to complete.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Invalid or expired OTP',
        variant: 'destructive'
      });
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!emailOtpVerified) {
      toast({
        title: 'Error',
        description: 'Please verify your new email with OTP first',
        variant: 'destructive'
      });
      return;
    }

    setEmailChangeLoading(true);
    try {
      await emailChangeAPI.changeEmail(newEmail);
      toast({
        title: 'Email Changed',
        description: 'Your email has been updated. Please login with your new email.'
      });
      
      // Clear auth and redirect to login
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change email',
        variant: 'destructive'
      });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const resetEmailChange = () => {
    setShowEmailChange(false);
    setNewEmail('');
    setEmailOtp('');
    setEmailOtpSent(false);
    setEmailOtpVerified(false);
  };

  const handleClearCache = async () => {
    const confirmed = window.confirm(
      'This will clear all cached data and reload the app. You will remain logged in. Continue?'
    );
    
    if (confirmed) {
      toast({
        title: 'Clearing cache...',
        description: 'Please wait while we refresh the app.'
      });
      
      await clearAppCache();
      
      setTimeout(() => {
        forceReload();
      }, 1000);
    }
  };

  // Check for app updates
  const handleCheckForUpdates = async () => {
    setCheckingUpdate(true);
    try {
      const result = await checkForUpdates();
      setUpdateAvailable(result.updateAvailable);
      
      if (result.updateAvailable) {
        toast({
          title: 'Update Available!',
          description: `A new version (${result.currentVersion}) is available. Click "Update Now" to get the latest features.`
        });
      } else {
        toast({
          title: 'You\'re up to date!',
          description: `You have the latest version (${result.currentVersion}).`
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to check for updates. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setCheckingUpdate(false);
    }
  };

  // Apply the update
  const handleApplyUpdate = async () => {
    setApplyingUpdate(true);
    toast({
      title: 'Updating...',
      description: 'Please wait while we apply the update.'
    });
    
    try {
      await applyUpdate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to apply update. Please try again.',
        variant: 'destructive'
      });
      setApplyingUpdate(false);
    }
  };

  const handleToggleFinancials = async () => {
    if (!financialPassword) {
      toast({
        title: 'Password Required',
        description: 'Please enter your password to continue',
        variant: 'destructive'
      });
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/auth/toggle-financials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: financialPassword,
          hideFinancials: !currentUser?.hideFinancials
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update setting');
      }

      const data = await response.json();
      
      // Update current user in localStorage with correct key
      const updatedUser = data.user;
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      localStorage.setItem('user', JSON.stringify(updatedUser)); // For backward compatibility
      
      toast({
        title: 'Success',
        description: data.message
      });
      
      setShowFinancialToggle(false);
      setFinancialPassword('');
      
      // Reload page to apply changes across all components
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('Failed to toggle financials:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update setting',
        variant: 'destructive'
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200',
      staff: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200',
      master: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 border-purple-200'
    };
    return colors[role] || 'bg-gray-100 dark:bg-secondary text-gray-800 dark:text-foreground';
  };

  // Load subscription information from API for fresh data
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (!currentUser) return;
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const token = localStorage.getItem('token');
        
        // Fetch fresh subscription status from API
        const response = await fetch(`${API_URL}/api/subscriptions/status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
        if (response.ok) {
          const freshSubData = await response.json();
          const plans = getSubscriptionPlans();
          
          // Map plan IDs
          const planIdMap = {
            'PLAN_FREE_TRIAL': 'PLAN_TRIAL',
            'PLAN_FREE': 'PLAN_TRIAL',
            'PLAN_BASIC': 'PLAN_1_MONTH',
            'PLAN_PRO': 'PLAN_3_MONTHS'
          };
          
          const planId = freshSubData.plan;
          const mappedPlanId = planIdMap[planId] || planId;
          const plan = plans.find(p => p.id === mappedPlanId || p.id === planId);
          
          setSubscriptionInfo({
            status: freshSubData.status,
            plan: freshSubData.plan,
            planName: freshSubData.planName,
            startDate: freshSubData.startDate,
            expiryDate: freshSubData.expiryDate,
            daysRemaining: freshSubData.daysRemaining,
            paymentMethod: freshSubData.paymentMethod,
            dataInconsistent: freshSubData.dataInconsistent,
            planDetails: plan || {
              name: freshSubData.planName || 'Trial',
              price: 0,
              currency: '₹',
              durationType: 'days',
              duration: 15,
              features: ['Basic access', 'Limited features']
            }
          });
        } else {
          // Fallback to currentUser data if API fails
          const plans = getSubscriptionPlans();
          const planId = currentUser.subscription?.plan;
          const planIdMap = {
            'PLAN_FREE_TRIAL': 'PLAN_TRIAL',
            'PLAN_FREE': 'PLAN_TRIAL',
            'PLAN_BASIC': 'PLAN_1_MONTH',
            'PLAN_PRO': 'PLAN_3_MONTHS'
          };
          const mappedPlanId = planIdMap[planId] || planId;
          const plan = plans.find(p => p.id === mappedPlanId || p.id === planId);
          
          setSubscriptionInfo({
            ...currentUser.subscription,
            planDetails: plan || {
              name: 'Trial',
              price: 0,
              currency: '₹',
              durationType: 'days',
              duration: 15,
              features: ['Basic access', 'Limited features']
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch subscription info:', error);
        // Fallback to currentUser data
        if (currentUser?.subscription) {
          const plans = getSubscriptionPlans();
          const planId = currentUser.subscription.plan;
          const plan = plans.find(p => p.id === planId);
          setSubscriptionInfo({
            ...currentUser.subscription,
            planDetails: plan
          });
        }
      }

      // Load payment history
      const payments = getMockPayments();
      const userPayments = payments.filter(p => p.userId === currentUser.id);
      setPaymentHistory(userPayments);
    };
    
    fetchSubscriptionInfo();
  }, [currentUser]);

  const getSubscriptionStatusBadge = (status, cancelled) => {
    if (cancelled && status === 'active') {
      return <Badge className="bg-orange-50 dark:bg-orange-900/200">Cancelled (Active until expiry)</Badge>;
    }
    const badges = {
      active: <Badge className="bg-green-50 dark:bg-green-900/200">Active</Badge>,
      pending: <Badge className="bg-yellow-50 dark:bg-yellow-900/200">Pending Approval</Badge>,
      expired: <Badge className="bg-red-50 dark:bg-red-900/200">Expired</Badge>,
      rejected: <Badge className="bg-gray-50 dark:bg-secondary0">Rejected</Badge>,
    };
    return badges[status] || <Badge className="bg-gray-400">No Subscription</Badge>;
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const result = await subscriptionAPI.cancelSubscription();
      
      // Update local subscription info
      setSubscriptionInfo(prev => ({
        ...prev,
        cancelled: true
      }));
      
      toast({
        title: 'Subscription Cancelled',
        description: result.canUseUntil 
          ? `You can continue using the app until ${new Date(result.canUseUntil).toLocaleDateString('en-IN')}`
          : 'Your subscription has been cancelled.',
      });
      
      setCancelDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel subscription',
        variant: 'destructive'
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const getDaysRemaining = () => {
    // Use API-provided daysRemaining if available (most accurate)
    if (subscriptionInfo?.daysRemaining !== undefined) {
      return subscriptionInfo.daysRemaining;
    }
    // Fallback to calculation from expiry date
    if (!subscriptionInfo?.expiryDate) return null;
    const expiry = new Date(subscriptionInfo.expiryDate);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Return to Master Button */}
      {impersonating && (
        <Card className="border-2 border-purple-200 bg-purple-50 dark:bg-purple-900/20">
          <CardContent className="p-4 sm:pt-6 sm:pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-3">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-purple-900 text-sm sm:text-base">Viewing as {currentUser?.name}</p>
                  <p className="text-xs sm:text-sm text-purple-700">You are currently impersonating this user</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  returnToMaster();
                  navigate('/subscriptions');
                  toast({
                    title: 'Returned to Master Account',
                    description: 'You are now back in your master account'
                  });
                }}
                className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto text-sm"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Master
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">Profile Settings</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-muted-foreground mt-1">Manage your account and team</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-muted/50 w-full lg:w-auto lg:inline-flex">
          <TabsTrigger value="profile" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
            <User className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">My </span>Profile
          </TabsTrigger>
          {(currentUser?.role === 'admin' || currentUser?.isMaster) && (
            <TabsTrigger value="staff" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
              <UsersIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Staff
            </TabsTrigger>
          )}
          {currentUser?.role === 'admin' && (
            <TabsTrigger value="activity" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Activity </span>Log
            </TabsTrigger>
          )}
          {(currentUser?.role === 'admin' || currentUser?.isMaster) && (
            <TabsTrigger value="system" className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2">
              <Database className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">System </span>Settings
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profile" className="space-y-4 sm:space-y-6 mt-4 sm:mt-6">{/* Profile content */}

      {/* Profile Card */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base sm:text-lg text-gray-900 dark:text-foreground">Account Information</CardTitle>
            {!isEditing && (
              <Button 
                onClick={() => setIsEditing(true)}
                variant="outline"
                size="sm"
                className="hover:bg-teal-50 dark:bg-teal-900/20 hover:border-teal-300 hover:text-teal-700 transition-colors text-xs sm:text-sm"
              >
                Edit Profile
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 lg:p-8">
          {/* Profile Avatar */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center text-white text-2xl sm:text-3xl lg:text-4xl font-bold shadow-lg flex-shrink-0">
              {currentUser?.name?.charAt(0) || 'U'}
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground">{currentUser?.name}</h2>
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 mt-2">
                <Badge className={getRoleBadgeColor(currentUser?.role)}>
                  <Shield className="w-3 h-3 mr-1" />
                  {currentUser?.role?.toUpperCase()}
                </Badge>
                {/* Subscription Badge */}
                {currentUser?.role === 'admin' && (
                  <SubscriptionBadge subscription={currentUser?.subscription} />
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-muted-foreground mt-2 truncate max-w-[200px] sm:max-w-none">ID: {currentUser?.id?.slice(0, 8)}...</p>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
                  <span>Full Name</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-secondary rounded-md border">
                    {currentUser?.name}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
                  <span>Email Address</span>
                </Label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-secondary rounded-md border flex items-center justify-between">
                  <span>{currentUser?.email}</span>
                  {!showEmailChange && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmailChange(true)}
                      className="text-teal-600 hover:text-teal-700 h-6 px-2"
                    >
                      <KeyRound className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                  )}
                </div>
                
                {/* Email Change with OTP Section */}
                {showEmailChange && (
                  <div className="mt-3 p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-800">Change Email Address</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={resetEmailChange}
                        className="h-6 px-2 text-gray-500 dark:text-muted-foreground"
                      >
                        ✕
                      </Button>
                    </div>
                    
                    {!emailOtpVerified ? (
                      <>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            placeholder="Enter new email address"
                            value={newEmail}
                            onChange={(e) => {
                              setNewEmail(e.target.value);
                              setEmailOtpSent(false);
                              setEmailOtp('');
                            }}
                            disabled={emailOtpSent && !emailOtpVerified}
                            className="flex-1"
                          />
                          {!emailOtpSent && (
                            <Button
                              type="button"
                              onClick={handleSendEmailOtp}
                              disabled={emailOtpLoading || !newEmail}
                              className="bg-teal-600 hover:bg-teal-700"
                            >
                              {emailOtpLoading ? 'Sending...' : 'Send OTP'}
                            </Button>
                          )}
                        </div>
                        
                        {emailOtpSent && !emailOtpVerified && (
                          <>
                            <p className="text-xs text-green-700">
                              ✓ OTP sent to {newEmail}. Check your inbox.
                            </p>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                placeholder="Enter 4-digit OTP"
                                value={emailOtp}
                                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                maxLength={4}
                                className="flex-1"
                              />
                              <Button
                                type="button"
                                onClick={handleVerifyEmailOtp}
                                disabled={emailOtpLoading || emailOtp.length !== 4}
                                className="bg-teal-600 hover:bg-teal-700"
                              >
                                {emailOtpLoading ? 'Verifying...' : 'Verify'}
                              </Button>
                            </div>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              onClick={() => {
                                setEmailOtpSent(false);
                                setEmailOtp('');
                              }}
                              className="text-xs text-blue-600 p-0 h-auto"
                            >
                              ← Change email or resend OTP
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Email verified: {newEmail}</span>
                        </div>
                        <Button
                          type="button"
                          onClick={handleChangeEmail}
                          disabled={emailChangeLoading}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          {emailChangeLoading ? 'Changing Email...' : 'Confirm Email Change'}
                        </Button>
                        <p className="text-xs text-amber-700">
                          ⚠️ You will be logged out after changing email.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
                  <span>Phone Number (10 digits)</span>
                </Label>
                {isEditing ? (
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    maxLength="10"
                  />
                ) : (
                  <div className="px-3 py-2 bg-gray-50 dark:bg-secondary rounded-md border">
                    {currentUser?.phone}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
                  <span>Role</span>
                </Label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-secondary rounded-md border">
                  <Badge className={getRoleBadgeColor(currentUser?.role)}>
                    {currentUser?.role?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              {/* Change Password Section - Only in Edit Mode */}
              {isEditing && (
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center space-x-2">
                      <Lock className="w-4 h-4 text-gray-500 dark:text-muted-foreground" />
                      <span>Change Password</span>
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPasswordChange(!showPasswordChange)}
                      className="text-teal-600 hover:text-teal-700"
                    >
                      {showPasswordChange ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  
                  {showPasswordChange && (
                    <div className="space-y-3 p-4 bg-gray-50 dark:bg-secondary rounded-lg border">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword" className="text-sm">Current Password *</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          placeholder="Enter current password"
                          value={passwordFormData.currentPassword}
                          onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword" className="text-sm">New Password *</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          placeholder="Min. 6 characters"
                          value={passwordFormData.newPassword}
                          onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password *</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Re-enter new password"
                          value={passwordFormData.confirmPassword}
                          onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground">
                        Leave blank if you do not want to change password
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 shadow-md"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Password Change - Master Only */}
      {currentUser?.isMaster && (
        <Card className="border-0 shadow-md">
          <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50 border-b p-4 sm:p-6">
            <CardTitle className="flex flex-wrap items-center gap-2 text-sm sm:text-base dark:text-foreground">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              <span>Change Password</span>
              <Badge className="bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200 text-xs">Master Only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {!showPasswordChange ? (
              <Button
                onClick={() => setShowPasswordChange(true)}
                variant="outline"
                size="sm"
                className="hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700 text-xs sm:text-sm"
              >
                Change Password
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordFormData.currentPassword}
                    onChange={(e) => setPasswordFormData({...passwordFormData, currentPassword: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordFormData.newPassword}
                    onChange={(e) => setPasswordFormData({...passwordFormData, newPassword: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordFormData.confirmPassword}
                    onChange={(e) => setPasswordFormData({...passwordFormData, confirmPassword: e.target.value})}
                    required
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordChange(false);
                      setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700"
                  >
                    Update Password
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* System Settings Card */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg dark:text-foreground">
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            System Settings
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Manage app cache and version information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
          {/* Device Security Link */}
          <Link to="/security" className="block">
            <div className="flex items-center justify-between p-3 sm:p-4 border-2 border-teal-200 rounded-lg bg-teal-50 dark:bg-teal-900/20 hover:bg-teal-100 dark:bg-teal-900/30 transition-colors cursor-pointer group">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg group-hover:bg-teal-200 transition-colors">
                  <Smartphone className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-teal-900 text-sm sm:text-base">Device Security</p>
                  <p className="text-xs sm:text-sm text-teal-700">Manage active sessions & logged in devices</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-teal-600 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
          
          {/* App Version with Update Option */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg gap-3">
            <div className="flex-1">
              <p className="font-medium text-sm sm:text-base">App Version</p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">
                Current: v{getAppVersion()}
                {updateAvailable && (
                  <span className="ml-2 text-green-600 font-medium">• Update available!</span>
                )}
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button 
                onClick={handleCheckForUpdates}
                variant="outline"
                className="flex-1 sm:flex-none text-xs sm:text-sm"
                size="sm"
                disabled={checkingUpdate}
              >
                {checkingUpdate ? (
                  <>
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                    Checking...
                  </>
                ) : (
                  'Check for Updates'
                )}
              </Button>
              {updateAvailable && (
                <Button 
                  onClick={handleApplyUpdate}
                  className="flex-1 sm:flex-none text-xs sm:text-sm bg-green-600 hover:bg-green-700"
                  size="sm"
                  disabled={applyingUpdate}
                >
                  {applyingUpdate ? (
                    <>
                      <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Now'
                  )}
                </Button>
              )}
            </div>
          </div>
          
          {/* Push Notifications Toggle */}
          {pushSupported && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg gap-3">
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-foreground flex items-center gap-2 text-sm sm:text-base">
                  {pushEnabled ? (
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-teal-600" />
                  ) : (
                    <BellOff className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-gray-400 dark:text-muted-foreground" />
                  )}
                  <span>Push Notifications</span>
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">
                  {pushPermission === 'denied' 
                    ? 'Blocked in browser settings' 
                    : pushEnabled 
                      ? 'Receive alerts on your device' 
                      : 'Get notified about important updates'}
                </p>
              </div>
              <Button 
                onClick={handleTogglePushNotifications}
                variant="outline"
                className="w-full sm:w-auto text-xs sm:text-sm"
                size="sm"
                disabled={pushLoading || pushPermission === 'denied'}
              >
                {pushLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : pushEnabled ? (
                  'Disable'
                ) : (
                  'Enable'
                )}
              </Button>
            </div>
          )}
          
          {/* Financial Visibility Toggle - Personal (Hidden for staff when admin has hidden financials) */}
          {currentUser?.role !== 'staff' && currentUser?.role !== 'receptionist' && !(isStaffUser && adminHidesFromStaff) && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg gap-3">
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-foreground flex items-center gap-2 text-sm sm:text-base">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Hide Financials <span className="hidden sm:inline">(Personal)</span></span>
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">
                  {currentUser?.hideFinancials 
                    ? 'Currently hidden' 
                    : 'Currently visible'}
                </p>
              </div>
              <Button 
                onClick={() => setShowFinancialToggle(true)}
                variant="outline"
                className="w-full sm:w-auto text-xs sm:text-sm"
                size="sm"
              >
                {currentUser?.hideFinancials ? 'Show' : 'Hide'}
              </Button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg gap-2">
            <div>
              <p className="font-medium text-sm sm:text-base">Clear Cache</p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">Clear cached files and refresh</p>
            </div>
            <Button 
              onClick={handleClearCache}
              variant="outline"
              className="flex items-center gap-2 w-full sm:w-auto text-xs sm:text-sm"
              size="sm"
            >
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
              Clear Cache
            </Button>
          </div>

          <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg">
            <p className="text-xs sm:text-sm text-blue-800">
              <strong>Tip:</strong> Clear cache if you&apos;re not seeing latest features.
            </p>
          </div>

          {/* Contact Us */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg gap-2 bg-gradient-to-r from-teal-50 to-cyan-50">
            <div>
              <p className="font-medium text-sm sm:text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-teal-600" />
                Contact Us
              </p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground">Need help? Reach out to our support team</p>
            </div>
            <a 
              href="mailto:info@myvenuemate.in"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md text-sm font-medium transition-colors w-full sm:w-auto"
            >
              <Mail className="w-4 h-4" />
              info@myvenuemate.in
            </a>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {(currentUser?.role === 'admin' || currentUser?.isMaster) && (
          <TabsContent value="staff" className="mt-6">
            <StaffManagement />
          </TabsContent>
        )}

        {currentUser?.role === 'admin' && (
          <TabsContent value="activity" className="mt-6">
            <ActivityLog />
          </TabsContent>
        )}

        {(currentUser?.role === 'admin' || currentUser?.isMaster) && (
          <TabsContent value="system" className="mt-6">
            <BackupRestore />
          </TabsContent>
        )}
      </Tabs>

      {/* Financial Toggle Dialog */}
      {showFinancialToggle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg p-4 sm:p-6 max-w-md w-full">
            <h3 className="text-base sm:text-lg font-medium mb-3 sm:mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-muted-foreground flex-shrink-0" />
              <span>{currentUser?.hideFinancials ? 'Show' : 'Hide'} Financials</span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mb-3 sm:mb-4">
              {currentUser?.hideFinancials 
                ? 'This will display amounts, payments, and revenue.' 
                : 'This will hide all financial data for security.'}
            </p>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label htmlFor="financialPassword" className="text-xs sm:text-sm">Password *</Label>
                <Input 
                  id="financialPassword"
                  type="password"
                  value={financialPassword}
                  onChange={(e) => setFinancialPassword(e.target.value)}
                  placeholder="Your account password"
                  className="mt-1 sm:mt-2 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleToggleFinancials()}
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-xs sm:text-sm"
                  onClick={() => {
                    setShowFinancialToggle(false);
                    setFinancialPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleToggleFinancials}
                  size="sm"
                  variant="outline"
                  className="text-xs sm:text-sm"
                >
                  {currentUser?.hideFinancials ? 'Show' : 'Hide'} Financials
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Dialog */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg p-4 sm:p-6 max-w-md w-full">
            <h3 className="text-base sm:text-lg font-bold mb-3 text-red-600 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Cancel Subscription
            </h3>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mb-4">
              Are you sure you want to cancel your subscription?
            </p>
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg mb-4">
              <p className="text-xs sm:text-sm text-amber-800">
                <strong>Note:</strong> You&apos;ll still have access to all features until <strong>{subscriptionInfo?.expiryDate ? new Date(subscriptionInfo.expiryDate).toLocaleDateString('en-IN') : 'expiry date'}</strong>. After that, you&apos;ll only be able to view your data.
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelLoading}
              >
                Keep Subscription
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                size="sm"
                variant="destructive"
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;